import type { Attachment } from '@medplum/fhirtypes';
import { cn } from '@liza/ui/utils';
import { decodeBase64Utf8 } from '../../utils/base64';
import { MessageResponse } from '../ui/shadcn-io/ai/message';

export type DocumentAttachmentProps = {
  attachment: Attachment;
  /**
   * Visible name for the download-link fallback, used only when the attachment has no `title`.
   * Typically the `DocumentReference.content[].format.display` (e.g. "Structured Body C-CDA 2.1"),
   * which names the document format when the attachment itself is untitled. Ignored by the inline
   * render modes (pdf/image/text/plain/text/html).
   */
  label?: string;
  /** Iframe height in pixels (PDF and HTML modes). Defaults to 600 to match the Medplum sample. */
  height?: number;
  /**
   * Make the iframe modes (PDF and HTML) fill their parent's height instead of using the fixed
   * `height`. Requires an ancestor chain with a definite height (e.g. a flex column). The iframe
   * scrolls its own content, so overflow stays inside the frame rather than the page.
   */
  fill?: boolean;
};

/**
 * Schemes we'll feed into an iframe `src`. `attachment.url` comes from a FHIR resource that
 * external imports can populate, so we treat it as untrusted. Anything outside this list
 * (`http:`, `javascript:`, `vbscript:`, `file:`, `data:text/html`, ...) is rejected before we
 * render — `http:` is excluded so PDFs are only ever fetched over TLS.
 */
const SAFE_PDF_URL_PATTERN = /^(https:|blob:|data:application\/pdf;)/i;

/**
 * Schemes we'll load into the sandboxed rendering iframe (`text/html` notes, url-backed text
 * documents). The iframe is `sandbox=""` so even a malicious payload can't execute scripts or
 * reach this page's DOM, but we still gate on scheme as defense-in-depth and to keep
 * `javascript:`/`file:` URLs from ever reaching the loader. `data:` is intentionally excluded —
 * inline content belongs in `attachment.data` (handled via `srcDoc` for HTML), not smuggled
 * through a `url`. `http:` is excluded too; remote content must be served over TLS.
 */
const SAFE_FRAME_URL_PATTERN = /^(https:|blob:)/i;

/** Content types we decode and render inline as text — `text/plain` verbatim, `text/markdown` formatted. */
const isInlineTextType = (contentType: string | undefined): boolean =>
  Boolean(contentType?.startsWith('text/plain') || contentType?.startsWith('text/markdown'));

/** Constrains Streamdown's heading/list scale to a clinical note; styled in component-overrides.css. */
const MARKDOWN_DOCUMENT_CLASS = 'liza-markdown-document';

/**
 * Render decoded inline text. `text/markdown` goes through `MessageResponse` (the Streamdown-backed
 * renderer the app already uses for AI output), so headings/bold/lists render formatted; everything
 * else is shown verbatim in a pre-wrapped paragraph.
 *
 * Safe for untrusted `attachment.data` (e.g. external-EHR imports, not just AI notes): Streamdown's
 * default rehype pipeline runs `rehype-sanitize` (hast-util-sanitize `defaultSchema` — strips
 * `<script>`/`<iframe>`, event-handler attributes, and unsafe URL protocols) plus `rehype-harden`,
 * and `MessageResponse` doesn't override `rehypePlugins`, so embedded raw HTML can't execute. This
 * mirrors the `sandbox=""` guard on the `text/html` path below.
 */
const renderInlineText = (contentType: string | undefined, data: string): React.ReactElement => (
  // Document text is patient free text — data-phi lets a "Hide PHI" mode blur it.
  <div data-phi>
    {contentType?.startsWith('text/markdown') ? (
      <MessageResponse className={MARKDOWN_DOCUMENT_CLASS}>{decodeBase64Utf8(data)}</MessageResponse>
    ) : (
      <p className="text-sm whitespace-pre-wrap">{decodeBase64Utf8(data)}</p>
    )}
  </div>
);

/**
 * Render untrusted content in a maximally-restricted iframe. Serves `text/html` (either a
 * `url` or inline `data` via `srcDoc`) and url-backed `text/plain`/`text/markdown` documents
 * (`url` only — inline text renders via `renderInlineText` instead). `attachment.url`/`data`
 * carries third-party content — encounter notes imported from external EHRs, user uploads,
 * etc. — so we treat it as untrusted. `sandbox=""` (no allowances) gives the frame an opaque
 * origin and blocks scripts, forms, popups, top-level navigation, and same-origin access;
 * malicious content cannot read this page's cookies/DOM or run JS.
 * `referrerPolicy="no-referrer"` avoids leaking the parent URL to whatever the iframe loads.
 * URL scheme is whitelisted as defense-in-depth.
 */
const renderSandboxedFrame = ({
  url,
  data,
  title,
  height,
  fill,
}: Pick<Attachment, 'url' | 'data' | 'title'> & { height: number; fill: boolean }): React.ReactElement | null => {
  // When `url` is present but its scheme is rejected, we fall back to inline `data` (if any)
  // rather than returning null — sandbox="" makes both paths equally safe to render.
  const src = url && SAFE_FRAME_URL_PATTERN.test(url) ? url : undefined;
  const srcDoc = !src && data ? decodeBase64Utf8(data) : undefined;
  if (!src && srcDoc === undefined) return null;
  return (
    <div
      className={cn('overflow-hidden rounded-sm border', fill && 'h-full')}
      style={fill ? undefined : { height }}
      data-phi="media"
    >
      <iframe
        title={title ?? 'Attachment'}
        src={src}
        srcDoc={srcDoc}
        sandbox=""
        referrerPolicy="no-referrer"
        className="size-full"
      />
    </div>
  );
};

/**
 * Render `application/pdf` content in an iframe (the browser's built-in PDF viewer). Prefers `url`
 * (Medplum-hosted Binary), falling back to inline base64 `data` via a `data:` URL so
 * DocumentReferences POSTed with `attachment.data` still render without a separate Binary.
 * `#navpanes=0` is a PDF-viewer hint that hides the toolbar; it only works reliably on `https:` URLs
 * (Chrome ignores it on `data:` URLs and Firefox's pdf.js may too), so only append it on the
 * url-backed path.
 */
const renderPdf = ({
  url,
  data,
  title,
  height,
  fill,
}: Pick<Attachment, 'url' | 'data' | 'title'> & { height: number; fill: boolean }): React.ReactElement | null => {
  const pdfSrc = url ? `${url}#navpanes=0` : data ? `data:application/pdf;base64,${data}` : undefined;
  if (!pdfSrc || !SAFE_PDF_URL_PATTERN.test(pdfSrc)) return null;
  return (
    <div
      className={cn('overflow-hidden rounded-sm border', fill && 'h-full')}
      style={fill ? undefined : { height }}
      data-phi="media"
    >
      {/*
        No `sandbox` attribute: Chrome refuses to initialize its built-in PDF viewer inside
        an opaque-origin iframe (shows a "This page has been blocked by Chrome" interstitial
        for any non-empty sandbox value, including `allow-scripts`). The PDF viewer itself
        (PDFium in Chrome, pdf.js in Firefox) is already sandboxed by the browser — embedded
        PDF JS can't access this page's DOM, cookies, or storage. The remaining attack
        surface (HTML payload mislabeled as `application/pdf`, malicious `attachment.url`) is
        handled by `SAFE_PDF_URL_PATTERN` above and by the PDF viewer rejecting non-PDF
        bytes outright (it does not fall back to HTML rendering).
      */}
      <iframe title={title ?? 'Attachment'} src={pdfSrc} className="size-full" allowFullScreen />
    </div>
  );
};

/**
 * Render a FHIR `Attachment` (typically the `content[].attachment` payload of a
 * `DocumentReference`) inline. Picks one of five modes based on `contentType`:
 *
 * - `application/pdf` → iframe (PDF viewer); supports either `url` or inline base64 `data`.
 * - `image/*` → `<img>` (requires `url`).
 * - `text/plain` / `text/markdown` with inline base64 `data` → decoded text; markdown renders
 *   formatted via `MessageResponse`, plain text verbatim.
 * - `text/plain` / `text/markdown` with only a `url` → sandboxed iframe of the URL (see the
 *   mode comment in the component body).
 * - `text/html` → sandboxed iframe; supports either `url` or inline base64 `data`.
 * - Anything else → download link using `attachment.url` (labeled `title ?? label ?? url`).
 *
 * Returns `null` when neither `url` nor inline `data` is available for the chosen mode.
 */
export const DocumentAttachment = ({
  attachment,
  label,
  height = 600,
  fill = false,
}: DocumentAttachmentProps): React.ReactElement | null => {
  const { url, title, contentType, data } = attachment;

  if (isInlineTextType(contentType)) {
    if (data) return renderInlineText(contentType, data);
    // Url-backed text: the payload lives behind `url` (typically a Binary's presigned storage
    // URL) rather than inline. The storage origin serves no CORS headers (a JS fetch() of the
    // payload is blocked on hosted Medplum) but display-only embeds are CORS-exempt — the same
    // mechanism the PDF and image modes rely on — so frame the URL directly. Trade-off:
    // browser-default typography, no app theming or markdown formatting. A `url` that fails the
    // scheme whitelist deliberately renders nothing rather than falling through to the
    // download-link anchor — a clickable link to a rejected scheme (`javascript:`, `http:`, ...)
    // is worse than a blank pane, and real presigned storage URLs are always `https:`.
    return renderSandboxedFrame({ url, title, height, fill });
  }

  if (contentType?.startsWith('text/html')) {
    return renderSandboxedFrame({ url, data, title, height, fill });
  }

  if (contentType?.startsWith('application/pdf')) {
    return renderPdf({ url, data, title, height, fill });
  }

  if (!url) return null;

  if (contentType?.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={title ?? ''}
        className="max-w-full rounded-sm border"
        data-phi="media"
        data-testid="document-attachment-image"
      />
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-phi>
      {title ?? label ?? url}
    </a>
  );
};
